import { equal, handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { SendTestEmailFormValues } from '@/client/campaigns';
import { CourseEntityEnrollEnum } from '@/client/courses';
import { SecurityCultureSendSurveyFormValues } from '@/client/security-culture/types';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { BlockEmailsBanner } from '@/components/block-emails';
import {
  SecurityCultureSendSurveyFormV2,
  SurveySentConfirmation,
} from '@/components/security-culture';
import {
  AutoScheduleBadge,
  AutoScheduleDescription,
  AutoScheduleLabel,
  PlannerCardTop,
} from '@/components/security-culture/PlannerCard.styled';
import { useSendEmail, useSurveys } from '@/hooks/query/security-culture.hooks';
import { useAppSelector } from '@/hooks/store';
import { useScAutoSchedule } from '@/hooks/useScAutoSchedule';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import {
  ComputerIconPath,
  isScPayingCustomer,
  showBlockEmailsMessage,
} from '@/utils/helpers';
import { getTargetEntityIdsByType } from '@/utils/targets';
import { AxiosError } from 'axios';
import { noop } from 'lodash';
import { Tooltip } from 'primereact/tooltip';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { v4 } from 'uuid';

const pathItems = (account: Account, t: TranslationFunctionType) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('security.culture'),
    url: RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY_PLANNER](),
    template: AppBreadCrumbTemplate,
    className: 'active',
  },
  {
    label: t('security.culture.send.survey'),
    url: RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SEND_SURVEY_V2](),
    template: AppBreadCrumbTemplate,
  },
];

const PlannerCombinedCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: var(--semi-big-padding);
  flex: 1;
`;

const DimmedContent = styled.div`
  opacity: 0.6;
  display: flex;
  align-items: center;
  gap: var(--semi-medium-padding);
  flex: 1;
`;

const SendFormCardBottom = styled.div<{ hasTopCard?: boolean }>`
  background-color: var(--beige-main);
  border: 1px solid var(--gray-moderate);
  border-radius: ${({ hasTopCard }) =>
    hasTopCard
      ? '0 0 var(--semi-medium-border-radius) var(--semi-medium-border-radius)'
      : 'var(--semi-medium-border-radius)'};
  padding: var(--xlarge-padding) var(--semi-big-padding);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--semi-big-padding);
  width: 100%;
  flex: 1;
`;

const FormCardTitle = styled.h2`
  font-size: var(--heading-medium-font-size);
  font-weight: 700;
  color: var(--black-main);
  margin: 0;
  text-align: center;
`;

const FormCardDescription = styled.p`
  font-size: var(--medium-font-size);
  color: var(--main-text-color);
  margin: 0;
  text-align: center;
`;

export const SecurityCultureSendSurveyPageV2: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as { confirmedFor?: string } | null;
  const currentAccount = useAppSelector(selectCurrentAccount);
  const toast = useToast();

  const [emailId, setEmailId] = useState<string | undefined>(undefined);
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(
    routeState?.confirmedFor ? new Date(routeState.confirmedFor) : undefined,
  );
  const [isConfirmed, setIsConfirmed] = useState<boolean>(
    !!routeState?.confirmedFor,
  );

  const isPaying = isScPayingCustomer(currentAccount);

  const { autoScheduleEnabled, toggleAutoSchedule } =
    useScAutoSchedule(isPaying);

  const { surveys, isLoading: isLoadingSurveys } = useSurveys(
    { take: 1, filters: [equal('isDemo', false)] },
    currentAccount?.id,
  );
  const hasSurveys = !isLoadingSurveys && (surveys?.count ?? 0) > 0;

  const showAutoScheduleCard = !isPaying || (isPaying && !hasSurveys);

  const sendSurveyEmails = useSendEmail();

  const sendEmails = async (
    data: SecurityCultureSendSurveyFormValues,
    resetForm?: () => void,
  ): Promise<boolean> => {
    const emailUuid = v4();
    setEmailId(emailUuid);

    try {
      if (!currentAccount) return false;

      const target = data?.target
        ? { [(data.target as any).type]: [data.target.id] }
        : {};

      const targets = data?.targets?.every(({ id }) => id !== currentAccount.id)
        ? {
            branches: getTargetEntityIdsByType(
              data.targets || [],
              CourseEntityEnrollEnum.BRANCHES,
            ),
            groups: getTargetEntityIdsByType(
              data.targets || [],
              CourseEntityEnrollEnum.GROUPS,
            ),
          }
        : {};

      await sendSurveyEmails.send({
        accountId: currentAccount.id as string,
        ...targets,
        ...target,
        typeFormId: data.typeFormId ?? undefined,
        url: data.url ?? undefined,
        scheduledFor: data.scheduledFor,
        test: data.test
          ? {
              email: data.test.email,
              firstName: data.test.firstName,
              lastName: data.test.lastName,
              language: data.test.language,
            }
          : undefined,
        emailId: emailUuid,
      });

      if (!data.test?.email) {
        toast?.success(
          t('toast.success'),
          data.scheduledFor
            ? t('security.culture.emailsScheduled')
            : t('security.culture.emailsQueued'),
          5000,
        );
      }

      return true;
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
      return false;
    } finally {
      if (resetForm) resetForm();
      setEmailId(undefined);
    }
  };

  const handleFormSubmit = async (
    data: SecurityCultureSendSurveyFormValues,
    resetForm: () => void,
  ) => {
    const success = await sendEmails(data, resetForm);
    if (success && data.scheduledFor) {
      navigate(location.pathname, {
        state: { confirmedFor: data.scheduledFor.toISOString() },
        replace: true,
      });
      setScheduledDate(data.scheduledFor);
      setIsConfirmed(true);
    }
  };

  const handleSendTestEmail = async (
    data: SecurityCultureSendSurveyFormValues,
    test: SendTestEmailFormValues,
  ) => {
    await sendEmails({
      branches: [],
      groups: [],
      users: [],
      typeFormId: data.typeFormId || undefined,
      url: data.url || undefined,
      test: {
        email: test.email,
        firstName: test.firstName,
        lastName: test.lastName,
        language: test.language,
      },
    });
  };

  const hasBlockEmailsBanner = showBlockEmailsMessage(currentAccount);

  if (isConfirmed && scheduledDate) {
    return (
      <FlexContainer
        direction="column"
        align="flex-start"
        flex="1"
        height="100%"
      >
        <AppBreadCrumb model={pathItems(currentAccount as Account, t)} />
        <SurveySentConfirmation
          scheduledDate={scheduledDate}
          onGoToDashboard={() =>
            navigate(RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY]())
          }
        />
      </FlexContainer>
    );
  }

  return (
    <FlexContainer direction="column" align="flex-start" flex="1" height="100%">
      <div className="w-full">
        {hasBlockEmailsBanner && <BlockEmailsBanner />}
        <AppBreadCrumb model={pathItems(currentAccount as Account, t)} />
        <h1 className="mb-0">{t('security.culture.survey.planner')}</h1>
      </div>

      <PlannerCombinedCard>
        {showAutoScheduleCard && (
          <PlannerCardTop>
            {!isPaying ? (
              <>
                <Tooltip
                  target="#send-auto-schedule-info"
                  position="top"
                  content={t(
                    'security.culture.send.survey.auto.schedule.tooltip.paying',
                  )}
                />
                <DimmedContent>
                  <AutoScheduleBadge>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <ComputerIconPath />
                    </svg>
                  </AutoScheduleBadge>
                  <FlexContainer
                    direction="column"
                    align="flex-start"
                    gap={1}
                    flex="1"
                    width="auto"
                  >
                    <AutoScheduleLabel>
                      {t('security.culture.planner.auto.schedule')}
                      <i
                        id="send-auto-schedule-info"
                        className="pi pi-info-circle"
                        style={{
                          fontSize: 'var(--small-font-size)',
                          cursor: 'pointer',
                          color: 'var(--main-text-color)',
                        }}
                      />
                    </AutoScheduleLabel>
                    <AutoScheduleDescription>
                      {t('security.culture.planner.auto.schedule.description')}
                    </AutoScheduleDescription>
                  </FlexContainer>
                  <Tooltip
                    target="#auto-schedule-toggle"
                    position="left"
                    content={t(
                      'security.culture.send.survey.auto.schedule.tooltip',
                    )}
                  />
                  <span
                    id="auto-schedule-toggle"
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <AppSwitch checked={false} disabled onChange={noop} />
                  </span>
                </DimmedContent>
              </>
            ) : (
              <>
                <Tooltip
                  target="#send-auto-schedule-info-paying"
                  position="top"
                  content={t(
                    'security.culture.send.survey.auto.schedule.tooltip.paying',
                  )}
                />
                <AutoScheduleBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <ComputerIconPath />
                  </svg>
                </AutoScheduleBadge>
                <FlexContainer
                  direction="column"
                  align="flex-start"
                  gap={1}
                  flex="1"
                  width="auto"
                >
                  <AutoScheduleLabel>
                    {t('security.culture.planner.auto.schedule')}
                    <i
                      id="send-auto-schedule-info-paying"
                      className="pi pi-info-circle"
                      style={{
                        fontSize: 'var(--small-font-size)',
                        cursor: 'pointer',
                        color: 'var(--main-text-color)',
                      }}
                    />
                  </AutoScheduleLabel>
                  <AutoScheduleDescription>
                    {t('security.culture.planner.auto.schedule.description')}
                  </AutoScheduleDescription>
                  {autoScheduleEnabled && (
                    <AutoScheduleDescription
                      style={{ marginTop: 'var(--xxsmall-padding)' }}
                    >
                      {t('security.culture.send.survey.comms.notice')}
                    </AutoScheduleDescription>
                  )}
                </FlexContainer>
                <AppSwitch
                  checked={autoScheduleEnabled}
                  onChange={(e) => toggleAutoSchedule(e.value)}
                />
              </>
            )}
          </PlannerCardTop>
        )}

        <SendFormCardBottom hasTopCard={showAutoScheduleCard}>
          <FlexContainer direction="column" align="center" gap={10}>
            <FormCardTitle>
              {t('security.culture.send.survey.plan.title')}
            </FormCardTitle>
            <FormCardDescription>
              {t('security.culture.send.survey.plan.description')}
            </FormCardDescription>
          </FlexContainer>

          {currentAccount && (
            <SecurityCultureSendSurveyFormV2
              onSubmit={handleFormSubmit}
              onSendTestEmail={handleSendTestEmail}
              loading={emailId ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
              simpleTestEmail={isPaying}
              submitLabel={t('security.culture.planner.schedule.survey')}
            />
          )}
        </SendFormCardBottom>
      </PlannerCombinedCard>
    </FlexContainer>
  );
};
